import { useState, useEffect } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  purple: "#7c3aed", purpleBg: "#fdf4ff", purpleBorder: "#e9d5ff",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Search:        <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Wrench:        <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    TrendingUp:    <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    ShieldCheck:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Leaf:          <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    MessageCircle: <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Heart:         <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    Bookmark:      <><path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></>,
    ChevronRight:  <><path d="m9 18 6-6-6-6"/></>,
    Lock:          <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    Flame:         <><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></>,
    PenLine:       <><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 19.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></>,
    X:             <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    BadgeCheck:    <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Category config ── */
const CATEGORIES = [
  { id: "all",          label: "All",                   icon: "Flame" },
  { id: "hiring",       label: "Hiring the right Pro",  icon: "Users" },
  { id: "pro-advice",   label: "Pro business advice",   icon: "TrendingUp" },
  { id: "spotlights",   label: "Success stories",       icon: "Star" },
  { id: "safety",       label: "Safety and trust",      icon: "ShieldCheck" },
  { id: "trends",       label: "Kenyan home trends",    icon: "Leaf" },
  { id: "maintenance",  label: "Home maintenance",      icon: "Home" },
];

/* ── Article / guide data ── */
const ARTICLES = [
  {
    id: 1,
    type: "guide",
    category: "hiring",
    title: "How to choose the right Pro for your home: a practical checklist",
    excerpt: "Reviews, response time, verified badges, service history. Here is exactly what to look at before you confirm a booking, and what each signal actually tells you.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "June 2026",
    readTime: "5 min read",
    tags: ["Hiring", "First-time clients"],
    featured: true,
  },
  {
    id: 2,
    type: "guide",
    category: "maintenance",
    title: "The Kenyan home maintenance calendar: what to service, and when",
    excerpt: "From roof inspection before the long rains to checking water tanks in the dry season, this month-by-month guide covers what your home needs and when to book it.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "May 2026",
    readTime: "7 min read",
    tags: ["Maintenance", "Planning"],
    featured: true,
  },
  {
    id: 3,
    type: "spotlight",
    category: "spotlights",
    title: "From casual odd jobs to KES 90,000 a month: James K.'s story",
    excerpt: "James started doing electrical work on referrals in Westlands. Eighteen months on JiraniApp later, he runs a two-person team and turns away work. He shares what changed.",
    author: "James K.",
    authorRole: "JiraniPro",
    authorLocation: "Westlands, Nairobi",
    date: "May 2026",
    readTime: "4 min read",
    tags: ["JiraniPro", "Success story"],
    featured: true,
  },
  {
    id: 4,
    type: "discussion",
    category: "pro-advice",
    title: "How do you handle clients who want to negotiate your rate after the job?",
    excerpt: "A Pro in Mombasa asked this and got 34 responses from other Pros across the platform. The thread covers scripts, policies, and one very direct answer from a veteran plumber.",
    author: "Ali H.",
    authorRole: "JiraniPro",
    authorLocation: "Nyali, Mombasa",
    date: "April 2026",
    readTime: "6 min read",
    tags: ["Pricing", "Client management"],
    replies: 34,
    featured: false,
  },
  {
    id: 5,
    type: "guide",
    category: "safety",
    title: "Before you let anyone into your home: the safety checklist every client should use",
    excerpt: "Verified badge, in-app booking confirmation, shared location. Five things worth checking before a Pro arrives, and why each one matters more than it might seem.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "April 2026",
    readTime: "4 min read",
    tags: ["Safety", "Clients"],
    featured: false,
  },
  {
    id: 6,
    type: "discussion",
    category: "trends",
    title: "Solar installations are booming in Nairobi. What that means for homeowners in 2026",
    excerpt: "Demand for solar panel installation has tripled on JiraniApp since January. We break down the costs, the payback period, and what to ask a Pro before signing anything.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "March 2026",
    readTime: "5 min read",
    tags: ["Trends", "Solar", "Nairobi"],
    featured: false,
  },
  {
    id: 7,
    type: "guide",
    category: "pro-advice",
    title: "Setting your rates on JiraniApp: what the data says about pricing and bookings",
    excerpt: "Pros who price within the platform median get 40% more first-time bookings than those who go significantly above it. Here is how to find your number without underselling yourself.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "March 2026",
    readTime: "6 min read",
    tags: ["Pricing", "JiraniPros"],
    featured: false,
  },
  {
    id: 8,
    type: "spotlight",
    category: "spotlights",
    title: "Mary W. built a repeat client base of 60 households in Kilimani. Here is how.",
    excerpt: "She does not advertise. She does not ask for referrals. Every client in her roster came through a completed JiraniApp booking and decided to keep booking her directly through the platform.",
    author: "Mary W.",
    authorRole: "JiraniPro",
    authorLocation: "Kilimani, Nairobi",
    date: "February 2026",
    readTime: "4 min read",
    tags: ["JiraniPro", "Cleaning", "Success story"],
    featured: false,
  },
  {
    id: 9,
    type: "guide",
    category: "hiring",
    title: "What to do when the work does not match what was agreed",
    excerpt: "A clear guide to raising a dispute, what evidence actually helps, and how to communicate with a Pro before escalating. Most issues can be resolved without a formal dispute.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "February 2026",
    readTime: "5 min read",
    tags: ["Disputes", "Clients"],
    featured: false,
  },
  {
    id: 10,
    type: "discussion",
    category: "maintenance",
    title: "Water tank cleaning in Mombasa: how often, and who to book?",
    excerpt: "Coastal homes with rooftop tanks need more frequent cleaning than most people do. A thread started by a client in Bamburi turned into a full guide with Pro responses and price benchmarks.",
    author: "Fatuma S.",
    authorRole: "Client",
    authorLocation: "Bamburi, Mombasa",
    date: "January 2026",
    readTime: "5 min read",
    tags: ["Maintenance", "Mombasa", "Water"],
    replies: 21,
    featured: false,
  },
  {
    id: 11,
    type: "guide",
    category: "trends",
    title: "Open-plan kitchens in Nairobi rentals: the renovation trend and its real cost",
    excerpt: "Landlords and homeowners are knocking down kitchen walls across Westlands and Kilimani. What it costs, what permits you need, and which trades to book in which order.",
    author: "JiraniApp Team",
    authorRole: "Platform",
    date: "January 2026",
    readTime: "6 min read",
    tags: ["Trends", "Renovation", "Nairobi"],
    featured: false,
  },
  {
    id: 12,
    type: "discussion",
    category: "safety",
    title: "How do you vet a client before accepting a booking? Pros share their approach.",
    excerpt: "A discussion thread started by a Pro in Karen asking how others assess new client bookings. 28 responses covering profile checks, location flags, and what a bad-faith booking usually looks like.",
    author: "Peter N.",
    authorRole: "JiraniPro",
    authorLocation: "Karen, Nairobi",
    date: "December 2025",
    readTime: "5 min read",
    tags: ["Safety", "JiraniPros"],
    replies: 28,
    featured: false,
  },
];

/* ── Discussion / community posts ── */
const DISCUSSIONS = [
  {
    id: 101,
    category: "pro-advice",
    title: "What's your policy on materials — do you source them or does the client?",
    author: "Samuel O.", authorRole: "JiraniPro", authorLocation: "Langata, Nairobi",
    preview: "I have been doing interior painting for 3 years and I still go back and forth on this. Sourcing materials myself gives me quality control but creates pricing conversations I would rather avoid...",
    replies: 19, likes: 41, date: "2 days ago",
  },
  {
    id: 102,
    category: "hiring",
    title: "Is it reasonable to ask a Pro for a written quote before booking?",
    author: "Grace N.", authorRole: "Client", authorLocation: "Westlands, Nairobi",
    preview: "I need painting done across 4 rooms and the prices vary a lot. Is it normal to request a proper written quote through the platform before I confirm? Has anyone done this?",
    replies: 14, likes: 27, date: "4 days ago",
  },
  {
    id: 103,
    category: "maintenance",
    title: "Rainy season prep: what to actually check before April",
    author: "David M.", authorRole: "Client", authorLocation: "Karen, Nairobi",
    preview: "Had serious roof drainage problems last April that I could have avoided. Sharing my checklist for this year and would love input from Pros who have seen what fails most often...",
    replies: 32, likes: 88, date: "5 days ago",
  },
  {
    id: 104,
    category: "trends",
    title: "Anyone in Mombasa seeing more demand for security systems after the Nyali incidents?",
    author: "Mohamed A.", authorRole: "JiraniPro", authorLocation: "Nyali, Mombasa",
    preview: "My security installation bookings have doubled since January. Wondering if other Pros in coastal cities are seeing the same and how you are handling the surge in quote requests...",
    replies: 11, likes: 33, date: "1 week ago",
  },
  {
    id: 105,
    category: "spotlights",
    title: "Six months in: what I have learned booking Pros through JiraniApp",
    author: "Aisha M.", authorRole: "Client", authorLocation: "Kondele, Kisumu",
    preview: "I have done 14 bookings since I started using JiraniApp in January. Here is what I do differently now versus what I did in my first few bookings, and the one mistake I will not make again...",
    replies: 23, likes: 67, date: "1 week ago",
  },
];

/* ── Avatar ── */
function Avatar({ name, size = 36 }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50,        text: T.g700,   border: T.g100 },
    { bg: "#eff6ff",    text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff",    text: "#7e22ce", border: "#e9d5ff" },
    { bg: "#fff7ed",    text: "#c2410c", border: "#fed7aa" },
    { bg: T.amberBg,    text: T.amber,   border: T.amberBorder },
  ];
  const c = palette[name.charCodeAt(0) % palette.length];
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.34, fontWeight: 600, color: c.text, fontFamily: T.sans, letterSpacing: "0.02em" }}>
      {initials}
    </div>
  );
}

/* ── Type badge ── */
function TypeBadge({ type }) {
  const config = {
    guide:      { label: "Guide",      color: T.blue,   bg: T.blueBg   },
    discussion: { label: "Discussion", color: T.purple, bg: T.purpleBg },
    spotlight:  { label: "Spotlight",  color: T.amber,  bg: T.amberBg  },
  };
  const c = config[type] || config.guide;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: c.color, background: c.bg, borderRadius: 99, padding: "3px 9px", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
      {c.label}
    </span>
  );
}

/* ── Role badge ── */
function RoleBadge({ role }) {
  const isPro = role === "JiraniPro";
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color: isPro ? T.g600 : T.n400, background: isPro ? T.g50 : T.n50, border: `1px solid ${isPro ? T.g100 : T.n150}`, borderRadius: 99, padding: "2px 7px", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
      {isPro ? "JiraniPro" : role}
    </span>
  );
}

/* ── Featured article card (large) ── */
function FeaturedCard({ article, onLoginPrompt }) {
  return (
    <div style={{ border: `1.5px solid ${T.n150}`, borderRadius: 18, padding: "28px", background: T.white, display: "flex", flexDirection: "column", gap: 16, cursor: "pointer", transition: "border-color .18s" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = T.g200}
      onMouseLeave={e => e.currentTarget.style.borderColor = T.n150}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <TypeBadge type={article.type} />
        <span style={{ fontSize: 11, color: T.n400 }}>{article.date}</span>
        <span style={{ fontSize: 11, color: T.n300 }}>·</span>
        <span style={{ fontSize: 11, color: T.n400 }}>{article.readTime}</span>
      </div>
      <div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(18px,3vw,24px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{article.title}</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>{article.excerpt}</p>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <Avatar name={article.author} size={32} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n800, lineHeight: 1.2 }}>{article.author}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <RoleBadge role={article.authorRole} />
              {article.authorLocation && <span style={{ fontSize: 11, color: T.n400 }}>{article.authorLocation}</span>}
            </div>
          </div>
        </div>
        <button onClick={onLoginPrompt} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.g600, fontSize: 13, fontWeight: 600, fontFamily: T.sans, padding: 0 }}>
          Read more <Icon name="ChevronRight" size={14} strokeWidth={2.5} color={T.g500} />
        </button>
      </div>
    </div>
  );
}

/* ── Article card (standard) ── */
function ArticleCard({ article, onLoginPrompt }) {
  return (
    <div style={{ border: `1px solid ${T.n150}`, borderRadius: 14, padding: "20px", background: T.white, display: "flex", flexDirection: "column", gap: 12, cursor: "pointer", transition: "border-color .15s, background .15s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.background = T.n50; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.n150; e.currentTarget.style.background = T.white; }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        <TypeBadge type={article.type} />
        <span style={{ fontSize: 11, color: T.n400 }}>{article.readTime}</span>
        {article.replies !== undefined && (
          <>
            <span style={{ fontSize: 11, color: T.n300 }}>·</span>
            <span style={{ fontSize: 11, color: T.n400, display: "flex", alignItems: "center", gap: 3 }}>
              <Icon name="MessageCircle" size={11} color={T.n400} strokeWidth={2} />{article.replies} replies
            </span>
          </>
        )}
      </div>
      <h4 style={{ fontFamily: T.serif, fontSize: "clamp(15px,2.5vw,18px)", fontWeight: 400, color: T.n900, margin: 0, letterSpacing: "-0.015em", lineHeight: 1.3 }}>{article.title}</h4>
      <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: 0 }}>{article.excerpt}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${T.n100}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Avatar name={article.author} size={26} />
          <span style={{ fontSize: 12, fontWeight: 500, color: T.n700 }}>{article.author}</span>
          <RoleBadge role={article.authorRole} />
        </div>
        <span style={{ fontSize: 11, color: T.n400 }}>{article.date}</span>
      </div>
    </div>
  );
}

/* ── Discussion card ── */
function DiscussionCard({ post, onLoginPrompt }) {
  return (
    <div style={{ border: `1px solid ${T.n150}`, borderRadius: 14, padding: "20px", background: T.white, display: "flex", flexDirection: "column", gap: 12, transition: "border-color .15s" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = T.n200}
      onMouseLeave={e => e.currentTarget.style.borderColor = T.n150}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <Avatar name={post.author} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{post.author}</span>
            <RoleBadge role={post.authorRole} />
            {post.authorLocation && (
              <span style={{ fontSize: 11, color: T.n400, display: "flex", alignItems: "center", gap: 3 }}>
                <Icon name="MapPin" size={10} strokeWidth={2} color={T.n400} />{post.authorLocation}
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: T.n400 }}>{post.date}</span>
        </div>
      </div>
      <div>
        <h4 style={{ fontFamily: T.serif, fontSize: "clamp(15px,2.5vw,17px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.015em", lineHeight: 1.35 }}>{post.title}</h4>
        <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: 0 }}>{post.preview}</p>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${T.n100}` }}>
        <div style={{ display: "flex", gap: 16 }}>
          <button onClick={onLoginPrompt} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = T.n700}
            onMouseLeave={e => e.currentTarget.style.color = T.n400}>
            <Icon name="MessageCircle" size={14} strokeWidth={1.8} />{post.replies} replies
          </button>
          <button onClick={onLoginPrompt} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = T.n700}
            onMouseLeave={e => e.currentTarget.style.color = T.n400}>
            <Icon name="Heart" size={14} strokeWidth={1.8} />{post.likes}
          </button>
        </div>
        <button onClick={onLoginPrompt} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 12, fontWeight: 600, fontFamily: T.sans, padding: "6px 12px", borderRadius: 8, transition: "all .15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          Join discussion <Icon name="ChevronRight" size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

/* ── Login prompt modal ── */
function LoginPrompt({ open, onClose, onLogin, onSignUp }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(15,17,23,.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 9999 }}>
      <style>{`@keyframes slideU2{from{transform:translateY(32px);opacity:0}to{transform:translateY(0);opacity:1}} @media(min-width:600px){.jrn-prompt{border-radius:16px!important;margin-bottom:40px!important}}`}</style>
      <div className="jrn-prompt" style={{ background: T.white, width: "100%", maxWidth: 480, borderRadius: "20px 20px 0 0", padding: "32px 24px 44px", animation: "slideU2 .24s cubic-bezier(.4,0,.2,1)", boxShadow: "0 -4px 40px rgba(0,0,0,.12)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="Users" size={20} color={T.g500} strokeWidth={1.7} />
          </div>
          <button onClick={onClose} style={{ background: T.n50, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="X" size={14} color={T.n600} strokeWidth={2} />
          </button>
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Join the conversation</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: "0 0 28px" }}>
          Reading is open to everyone. Sign in or create a free account to post, reply, and save content you want to come back to.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={onSignUp} style={{ padding: "13px", background: T.g500, color: T.white, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em" }}
            onMouseEnter={e => e.currentTarget.style.background = T.g600}
            onMouseLeave={e => e.currentTarget.style.background = T.g500}>
            Create a free account
          </button>
          <button onClick={onLogin} style={{ padding: "13px", background: "transparent", color: T.n700, border: `1.5px solid ${T.n200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.sans }}>
            Sign in to existing account
          </button>
        </div>
        <p style={{ fontSize: 12, color: T.n400, textAlign: "center", margin: "16px 0 0" }}>No subscription. No spam. Just the community.</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function CommunityHubPage() {
  useFonts();
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();

  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTab, setActiveTab]           = useState("articles");
  const [search, setSearch]                 = useState("");
  const [searchFocused, setSearchFocused]   = useState(false);
  const [loginPrompt, setLoginPrompt]       = useState(false);

  const goToLogin  = () => { setLoginPrompt(false); openLogin(); };
  const goToSignUp = () => { setLoginPrompt(false); navigate("/signup"); };

  const featured   = ARTICLES.filter(a => a.featured);
  const allArticles = ARTICLES.filter(a => !a.featured);

  const filteredArticles = allArticles.filter(a => {
    const catMatch = activeCategory === "all" || a.category === activeCategory;
    const searchMatch = search === "" ||
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(search.toLowerCase()) ||
      a.tags.some(t => t.toLowerCase().includes(search.toLowerCase()));
    return catMatch && searchMatch;
  });

  const filteredDiscussions = DISCUSSIONS.filter(d => {
    const catMatch = activeCategory === "all" || d.category === activeCategory;
    const searchMatch = search === "" ||
      d.title.toLowerCase().includes(search.toLowerCase()) ||
      d.preview.toLowerCase().includes(search.toLowerCase());
    return catMatch && searchMatch;
  });

  const featuredFiltered = featured.filter(a =>
    activeCategory === "all" || a.category === activeCategory
  );

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        .jrn-cat { background: none; border: none; cursor: pointer; font-family: ${T.sans}; transition: all .15s; }
        .jrn-tab { background: none; border: none; cursor: pointer; font-family: ${T.sans}; transition: all .15s; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => setLoginPrompt(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.g500, border: "none", cursor: "pointer", color: T.white, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 600 }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          <Icon name="PenLine" size={13} strokeWidth={2} color={T.white} />
          Post or reply
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,88px) 20px clamp(44px,6vw,72px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Users" size={14} color={T.g500} strokeWidth={2.2} />
            Community Hub
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,64px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.06, color: T.n900, letterSpacing: "-0.03em", maxWidth: 580 }}>
            Learn from those who've<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>been there</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 32px", maxWidth: 500 }}>
            Guides, real discussions, and Pro spotlights from across Kenya. Whether you're hiring your first Pro or running a full-time service business, there's something here for you.
          </p>
          {/* Search */}
          <div style={{ position: "relative", maxWidth: 480 }}>
            <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <Icon name="Search" size={17} color={T.n400} strokeWidth={1.8} />
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
              placeholder="Search guides, discussions, topics..."
              style={{ width: "100%", padding: "14px 44px 14px 46px", border: `1px solid ${searchFocused ? T.g500 : T.n200}`, borderRadius: 12, fontSize: 14, outline: "none", background: T.white, color: T.n900, fontFamily: T.sans, boxShadow: searchFocused ? `0 0 0 4px ${T.g100}` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .18s" }} />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: T.n100, border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="X" size={11} color={T.n600} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ══ STATS STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          {[
            { icon: "Users",         label: "Open to all clients and Pros" },
            { icon: "MessageCircle", label: "Active discussions every week" },
            { icon: "BadgeCheck",    label: "Pro-verified answers flagged clearly" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "20px", display: "flex", alignItems: "center", gap: 11, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={17} color={T.g500} strokeWidth={1.6} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ CATEGORY FILTER ══ */}
        <div style={{ paddingTop: "clamp(32px,5vw,48px)", marginBottom: 32 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CATEGORIES.map(cat => {
              const active = activeCategory === cat.id;
              return (
                <button key={cat.id} className="jrn-cat"
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 14px", borderRadius: 99,
                    border: `1.5px solid ${active ? T.g500 : T.n150}`,
                    background: active ? T.g500 : T.white,
                    color: active ? T.white : T.n600,
                    fontSize: 13, fontWeight: active ? 600 : 400,
                  }}>
                  <Icon name={cat.icon} size={13} color={active ? T.white : T.n400} strokeWidth={2} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ══ FEATURED ══ */}
        {featuredFiltered.length > 0 && search === "" && (
          <div style={{ marginBottom: "clamp(40px,6vw,56px)" }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 16px" }}>Featured</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
              {featuredFiltered.map(a => (
                <FeaturedCard key={a.id} article={a} onLoginPrompt={() => setLoginPrompt(true)} />
              ))}
            </div>
          </div>
        )}

        {/* ══ TABS ══ */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.n100}`, marginBottom: 28 }}>
          {[
            { id: "articles",     label: "Guides and articles" },
            { id: "discussions",  label: "Community discussions" },
          ].map(tab => (
            <button key={tab.id} className="jrn-tab"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "12px 18px", fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? T.n900 : T.n400,
                borderBottom: `2px solid ${activeTab === tab.id ? T.g500 : "transparent"}`,
                marginBottom: -1, letterSpacing: "-0.01em",
              }}>
              {tab.label}
              <span style={{ marginLeft: 7, fontSize: 11, fontWeight: 600, color: activeTab === tab.id ? T.g500 : T.n300, background: activeTab === tab.id ? T.g50 : T.n50, border: `1px solid ${activeTab === tab.id ? T.g100 : T.n150}`, borderRadius: 99, padding: "1px 7px" }}>
                {tab.id === "articles" ? filteredArticles.length : filteredDiscussions.length}
              </span>
            </button>
          ))}
        </div>

        {/* ══ ARTICLES TAB ══ */}
        {activeTab === "articles" && (
          <div>
            {filteredArticles.length === 0 ? (
              <div style={{ textAlign: "center", padding: "52px 0", color: T.n400 }}>
                <Icon name="Search" size={36} color={T.n200} strokeWidth={1} />
                <p style={{ fontSize: 15, fontWeight: 500, color: T.n600, margin: "14px 0 6px" }}>No articles match your search</p>
                <p style={{ fontSize: 13, margin: 0 }}>Try a different keyword or browse all categories.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                {filteredArticles.map(a => (
                  <ArticleCard key={a.id} article={a} onLoginPrompt={() => setLoginPrompt(true)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ DISCUSSIONS TAB ══ */}
        {activeTab === "discussions" && (
          <div>
            {/* Post CTA */}
            <div style={{ border: `1.5px dashed ${T.n200}`, borderRadius: 14, padding: "18px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, cursor: "pointer", background: T.n50, transition: "border-color .15s" }}
              onClick={() => setLoginPrompt(true)}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.g400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.n100, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="PenLine" size={15} color={T.n400} strokeWidth={1.8} />
              </div>
              <span style={{ fontSize: 14, color: T.n400, fontStyle: "italic" }}>Start a discussion or ask the community something...</span>
              <div style={{ marginLeft: "auto", flexShrink: 0, display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: T.n400 }}>
                <Icon name="Lock" size={12} color={T.n400} strokeWidth={2} />
                Sign in to post
              </div>
            </div>

            {filteredDiscussions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "52px 0", color: T.n400 }}>
                <Icon name="MessageCircle" size={36} color={T.n200} strokeWidth={1} />
                <p style={{ fontSize: 15, fontWeight: 500, color: T.n600, margin: "14px 0 6px" }}>No discussions match your search</p>
                <p style={{ fontSize: 13, margin: 0 }}>Try adjusting your filter or starting a new discussion.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {filteredDiscussions.map(d => (
                  <DiscussionCard key={d.id} post={d} onLoginPrompt={() => setLoginPrompt(true)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ FOOTER CTA ══ */}
        <div style={{ marginTop: "clamp(56px,8vw,80px)", padding: "clamp(32px,5vw,48px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Got something to <em style={{ fontStyle: "italic", color: T.g600 }}>share?</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 400 }}>
            The best content in this space comes from clients and Pros with real experience. Sign in to post, reply, or share a story.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSignUp} style={{ padding: "13px 28px", background: T.g500, color: T.white, border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em", display: "inline-flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Create a free account
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToLogin} style={{ padding: "13px 28px", background: "transparent", color: T.n700, border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Sign in
            </button>
          </div>
        </div>

      </div>

      <LoginPrompt
        open={loginPrompt}
        onClose={() => setLoginPrompt(false)}
        onLogin={goToLogin}
        onSignUp={goToSignUp}
      />
    </div>
  );
}